import { useUser } from '@clerk/clerk-react';
import { css } from '@styled-system/css';
import { stack } from '@styled-system/patterns';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AuthGuard } from '@/features/auth/components';
import { useSendContactForm } from '@/features/contact/hooks/useSendContactForm';
import { Button } from '@/shared/components/ui/Button';
import { Message } from '@/shared/components/ui/Message';
import { RoundedCloseButton } from '@/shared/components/ui/RoundedCloseButton';

export function ContactPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sendContactForm = useSendContactForm();
  const { user } = useUser();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [emailError, setEmailError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Pré-remplir l'email avec celui de l'utilisateur connecté
  useEffect(() => {
    if (user?.emailAddresses?.[0]?.emailAddress) {
      setEmail(user.emailAddresses[0].emailAddress);
    }
  }, [user]);

  const pageStyles = stack({
    direction: 'column',
    alignItems: 'center',
    backgroundColor: 'base.bg',
    minHeight: '100vh',
    width: '100vw',
    fontFamily: 'sans',
  });

  const closeButtonStyles = css({
    position: 'absolute',
    top: '1.9375rem',
    right: '1.9375rem',
  });

  const titleStyles = css({
    mt: '3.5rem',
    fontSize: '2xl', // 25px
    fontWeight: 'bold',
    color: 'font.primary',
    textAlign: 'center',
    lineHeight: '1.07',
  });

  const contactSectionStyles = css({
    mt: '1.88rem',
    w: '90%',
    maxW: '22.9375rem', // 367px
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: 0,
    py: '0.75rem', // 12px
    borderRadius: '0.5rem',
  });

  const discordTextStyles = css({
    fontSize: 'xs', // 10px
    fontWeight: 'medium',
    color: 'font.secondary',
    lineHeight: '1.07',
    '& .discord-username': {
      fontSize: 'md', // 13px
      fontWeight: 'bold',
      color: 'font.primary',
    },
  });

  const formContainerStyles = css({
    mt: '3.81rem',
    w: '90%',
    maxW: '41.4375rem', // 663px
    display: 'flex',
    flexDirection: 'column',
    gap: 'lg', // 35px
  });

  const inputGroupStyles = css({
    display: 'flex',
    flexDirection: 'column',
    gap: 'sm', // 10px
    width: '100%',
  });

  const labelStyles = css({
    fontSize: 'md', // 13px
    fontWeight: 'medium',
    color: 'font.primary',
    lineHeight: '1.07',
  });

  const inputStyles = css({
    width: '100%',
    px: 'sm', // 10px
    py: 'sm', // 10px
    fontSize: 'sm', // 11px
    fontWeight: 'medium',
    color: 'font.primary',
    backgroundColor: 'transparent',
    border: '0.046875rem solid',
    borderColor: 'base.border',
    borderRadius: 'sm', // 6px
    outline: 'none',
    fontFamily: 'sans',
    '&:focus': {
      borderColor: 'accent.primary',
    },
    '&::placeholder': {
      color: 'font.lowkey',
    },
  });

  const textareaStyles = css({
    width: '100%',
    px: 'sm', // 10px
    py: 'sm', // 10px
    fontSize: 'sm', // 11px
    fontWeight: 'medium',
    color: 'font.primary',
    backgroundColor: 'transparent',
    border: '0.046875rem solid',
    borderColor: 'base.border',
    borderRadius: 'sm',
    outline: 'none',
    fontFamily: 'sans',
    resize: 'vertical',
    height: '8.4375rem', // 135px du Figma
    '&:focus': {
      borderColor: 'accent.primary',
    },
    '&::placeholder': {
      color: 'font.lowkey',
    },
  });

  const submitButtonContainerStyles = css({
    mt: '3.94rem',
  });

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    // Effacer l'erreur si l'utilisateur tape à nouveau
    if (emailError) {
      setEmailError('');
    }
  };

  const handleDiscordClick = () => {
    const discordUrl = import.meta.env.VITE_DISCORD_INVITE_URL;
    window.open(discordUrl, '_blank', 'noopener,noreferrer');
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    // Validation finale avant soumission
    if (!validateEmail(email)) {
      setEmailError(t('contact.emailInvalid'));
      return;
    }

    // Effacer les messages précédents
    setEmailError('');
    setSuccessMessage('');

    try {
      await sendContactForm.mutateAsync({ email, message });

      // Afficher un message de succès
      setSuccessMessage(t('contact.success'));

      // Réinitialiser le formulaire après succès
      setEmail('');
      setMessage('');
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setEmailError(t('contact.sendError'));
    }
  };

  return (
    <AuthGuard>
      <div className={pageStyles}>
        {/* Close button */}
        <RoundedCloseButton className={closeButtonStyles} onClick={() => navigate('/')} />

        {/* Title */}
        <h1 className={titleStyles}>Contact</h1>

        {/* Discord contact section */}
        <div className={contactSectionStyles}>
          <div className={discordTextStyles}>
            <Trans i18nKey="contact.discordText" components={{ username: <span className="discord-username" /> }} />
          </div>
          <Button handleClick={handleDiscordClick}>{t('contact.ourDiscord')}</Button>
        </div>

        {/* Form container */}
        <div className={formContainerStyles}>
          <form onSubmit={handleSubmit}>
            {/* Email field */}
            <div className={inputGroupStyles}>
              <label className={labelStyles}>{t('contact.emailLabel')}</label>
              <input
                type="email"
                className={inputStyles}
                value={email}
                onChange={handleEmailChange}
                placeholder={t('contact.emailPlaceholder')}
                required
              />
              {emailError && (
                <Message type="error" className={css({ mt: 'xs' })}>
                  {emailError}
                </Message>
              )}
            </div>

            {/* Message field */}
            <div className={inputGroupStyles} style={{ marginTop: '2.1875rem' }}>
              <label className={labelStyles}>{t('contact.messageLabel')}</label>
              <textarea
                className={textareaStyles}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('contact.messagePlaceholder')}
                required
                maxLength={1500}
              />
            </div>
          </form>
        </div>

        {/* Success message */}
        {successMessage && (
          <Message type="info" className={css({ mt: 'sm', width: '41.4375rem' })}>
            {successMessage}
          </Message>
        )}

        {/* Submit button */}
        <div className={submitButtonContainerStyles}>
          <Button
            handleClick={handleSubmit}
            showLoader
            disabled={sendContactForm.isPending || !email || !message}
            className={css({
              width: '15.1875rem', // 243px
              height: '2.125rem', // 34px
              border: '0.03125rem solid',
              borderColor: 'base.border',
              borderRadius: 'sm',
              fontSize: 'sm', // 11px
              fontWeight: 'semibold',
              color: 'font.primary',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bg: 'accent.primary !important',
              _hover: {
                bg: 'accent.primaryHover !important',
              },
            })}
          >
            <div
              className={css({
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem',
                height: '0.5625rem',
              })}
            >
              {t('contact.submit')}
            </div>
          </Button>
        </div>
      </div>
    </AuthGuard>
  );
}
